import { isEmpty, split } from "lodash";
import { useRouter } from "next/router";
import { classNames } from "primereact/utils";
import { useTranslation } from "react-i18next";
import { useCompanies } from "../../../../common/api/useCompanies";
import usePermissions from "../../../../common/api/usePermissions";
import { PermissionsGate } from "../../../../common/components/PermissionsGate";
import {
    ADMIN_ROUTES,
    COMPANY_ROUTES,
    PERMISSIONS,
    THEMES,
} from "../../../../common/constants";
import { useRedirectStore } from "../../../../common/store/useRedirectStore";
import { useSetUpSpacesStore } from "../../../set-up-spaces-flow/store";
import { ActiveModeChip } from "../ActiveModeChip";
import { AddLocationButton } from "../AddLocationButton";
import { BetterspaceWordmark } from "../BetterspaceWordmark";
import { LocationSelection } from "../LocationSelection";
import { UserMenu } from "../UserMenu";
import { AdminChip } from "./AdminChip";

interface HeaderProps {
    onLogoutClick?: () => void;
    onMenuClick: () => void;
}

const Header = (props: HeaderProps) => {
    const { onLogoutClick = () => {}, onMenuClick } = props;

    const { t } = useTranslation("common");
    const router = useRouter();
    const { companyId, locationId } = router.query as {
        companyId?: string;
        locationId?: string;
    };

    const { lastLocationPath, setLastLocationPath } = useRedirectStore();

    const { toggleVisibility } = useSetUpSpacesStore();

    const { hasPermissions } = usePermissions();
    const { companies, isLoadingCompanies } = useCompanies();
    const isCompanyView = !!companyId && !locationId;
    const hasCompany = isLoadingCompanies || companies?.[0];
    const showActiveModeChip =
        !!locationId &&
        hasPermissions({
            permissions: [PERMISSIONS.ENERGY_MODE.READ],
            companyId,
            locationId,
        });

    const handleSwitchBetterspaceView = (checked: boolean) => {
        if (checked) {
            // Remove URL parameter before saving
            const splitRoute = split(router.asPath, "?");
            splitRoute?.[0] && setLastLocationPath(splitRoute?.[0]);
            router.push(ADMIN_ROUTES.SYSTEMCHECK);
            return;
        }
        if (!isEmpty(companies)) {
            if (lastLocationPath) {
                router.push({
                    pathname: lastLocationPath,
                });
                return;
            }
            // Until Company Dashboard page will be added
            const defaultCompanyRoute = COMPANY_ROUTES.LOCATIONS;
            router.push({
                pathname: defaultCompanyRoute,
                query: {
                    companyId: companies[0].id,
                },
            });
        }
    };

    return (
        <div
            className={classNames(
                "z-10 h-14 md:h-12.5 shrink-0 md:px-6 flex items-center justify-end transition-colors duration-300",
                {
                    "bg-tertiary-600": isCompanyView,
                    "bg-primary-500": !isCompanyView,
                }
            )}
        >
            <div className="flex min-w-0 grow items-center justify-end gap-0.5 md:gap-1.5">
                <button
                    type="button"
                    aria-label={t("common:expandSidebar")}
                    onClick={onMenuClick}
                    className="flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-lg text-primary-text hover:bg-surface-500/16 md:hidden"
                >
                    <i aria-hidden className="pi pi-bars text-[1.375rem]" />
                </button>
                <span className="mr-auto flex shrink-0 items-center gap-1.75 pl-1 md:hidden">
                    <BetterspaceWordmark
                        theme={THEMES.DARK}
                        iconClassName="[&>svg]:h-6.75"
                        textClassName="hidden sm:inline text-[0.9375rem] leading-none whitespace-nowrap text-primary-text"
                    />
                </span>
                {showActiveModeChip && (
                    <div className="hidden md:contents">
                        <ActiveModeChip className="mr-1.5" />
                    </div>
                )}
                {/* Hide companies dropdown in Betterspace Admin view */}
                {(hasPermissions() && !companyId) || !hasCompany ? null : (
                    <LocationSelection
                        companies={companies}
                        isLoadingCompanies={isLoadingCompanies}
                    />
                )}

                {!hasCompany && !hasPermissions() && (
                    <button onClick={toggleVisibility}>
                        <i className="pi pi-plus-circle text-lg text-primary-text"/>
                    </button>
                )}
                <div className="flex shrink-0 items-center gap-1 md:gap-1.5">
                    <div className="hidden md:contents">
                        <AddLocationButton />
                    </div>
                    <PermissionsGate>
                        <AdminChip
                            isChecked={!companyId}
                            onToggle={handleSwitchBetterspaceView}
                        />
                    </PermissionsGate>
                </div>

                <UserMenu onLogoutClick={onLogoutClick} />
            </div>
        </div>
    );
};

export default Header;
